
import {
  Box,
  Grid,
  TextField,
  InputAdornment,
  Typography,
  Button,
  TableRow,
  TableCell,
  Autocomplete
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import Table from '../../../components/Table';
import { Company } from '../../../schema/company';
import { TableResponse } from '../../../schema/helper';
import { Blacklist } from '../../../schema/blacklist';
import { api } from '../../../api';

const tableHead = [
  'เลขบัตรประชาชน',
  'ชื่อ นามสกุล',
  'ประเภทบุคคล',
  'บริษัท',
  'วันที่ลงประวัติ Blacklist',
  'วันที่สิ้นสุด Blacklist',
  'สถานะ',
  'Actions'
];

const TablePersonBlacklist = () => {
  const today = new Date();
  const [filterID, setFilterID] = useState<string>('');
  const [filterName, setFilterName] = useState<string>('');

  const [filterCompanyIDList, setFilterCompanyIDList] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [searchCompanyKeyword, setSearchCompanyKeyword] = useState<string>('');
  const [data, setData] = useState<TableResponse<Blacklist> | null>(null);
  const [isExportLoading, setIsExportLoading] = useState<boolean>(false);

  const handleExportCSVFile = () => {
    if (isExportLoading) {
      return;
    }
    const searchParams = new URLSearchParams();
    if (filterID) {
      searchParams.set('personal_id', filterID);
    }
    if (filterName) {
      searchParams.set('name', filterName);
    }

    if (filterCompanyIDList.length) {
      filterCompanyIDList.forEach((company_id) => {
        searchParams.append('company_id', `${company_id}`);
      });
    }
    setIsExportLoading(true);
    api
      .get(`/blacklist/export`, {
        params: searchParams,
        responseType: 'blob'
      })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'blacklist_export_data.xlsx');
        document.body.appendChild(link);
        link.click();
      })
      .finally(() => setIsExportLoading(false));
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  useEffect(() => {
    const timeOutId = setTimeout(() => {
      const searchParams = new URLSearchParams();
      if (filterID) {
        searchParams.set('personal_id', filterID);
      }
      if (filterName) {
        searchParams.set('name', filterName);
      }
      if (filterCompanyIDList.length) {
        filterCompanyIDList.forEach((company_id) => {
          searchParams.append('company_id', `${company_id}`);
        });
      }
      searchParams.set('offset', `${page * rowsPerPage}`);
      searchParams.set('limit', `${rowsPerPage}`);

      api
        .get<TableResponse<Blacklist>>(`/blacklist/table`, { params: searchParams })
        .then((res) => {
          const { data } = res;
          setData(data);
        });
    }, 500);
    return () => clearTimeout(timeOutId);
  }, [filterID, filterName, filterCompanyIDList, page, rowsPerPage]);

  useEffect(() => {
    api
      .get<Company[]>(`/company`, {
        params: {
          limit: 25
        }
      })
      .then((res) => {
        const { data } = res;
        setCompanies(data);
      });
  }, []);

  useEffect(() => {
    if (searchCompanyKeyword) {
      const timeOut = setTimeout(() => {
        const searchParams = new URLSearchParams();
        searchParams.set('company_name', searchCompanyKeyword);
        searchParams.set('limit', '25');

        api.get<Company[]>(`/company`, { params: searchParams }).then((res) => {
          const { data } = res;
          setCompanies(data);
        });
      }, 500);
      return () => clearTimeout(timeOut);
    }
  }, [searchCompanyKeyword]);
  return (
    <>
      <Grid container spacing={2} marginTop={1}>
        <Grid item md={3}>
          <TextField
            id="search-text-label"
            label="ค้นหาเลขบัตรประชาชน"
            variant="outlined"
            placeholder="ค้นหาเลขบัตรประชาชน"
            onChange={(event) => setFilterID(event.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            autoComplete="off"
          />
        </Grid>
        <Grid item md={3}>
          <TextField
            id="search-text-label"
            label="ค้นหาชื่อ - นามสกุล"
            variant="outlined"
            placeholder="ค้นหาชื่อ - นามสกุล"
            onChange={(event) => setFilterName(event.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            autoComplete={'off'}
          />
        </Grid>

        <Grid item md={3}>
          <Autocomplete
            multiple
            limitTags={2}
            disablePortal
            options={companies}
            getOptionLabel={(option) => option.company_name}
            renderInput={(params) => <TextField {...params} label="บริษัท" fullWidth />}
            onChange={(_, value) => setFilterCompanyIDList(value.map((data) => data.id.toString()))}
            onInputChange={(_, newInputValue) => {
              setSearchCompanyKeyword(newInputValue);
            }}
            fullWidth
          />
        </Grid>
        <Grid item md sx={{ display: 'flex', justifyContent: 'end' }}>
          <Button
            component={Link}
            to={`/blacklist/create`}
            variant="contained"
            color="primary"
            sx={{ marginRight: 1 }}
          >
            เพิ่มข้อมูล
          </Button>
          <Button variant="outlined" color="primary" onClick={handleExportCSVFile} disabled={isExportLoading}>
            Export
          </Button>
        </Grid>
      </Grid>
      <Box sx={{ padding: '24px 0' }}>
        <Table
          heads={tableHead}
          count={data?.total ? data?.total : 0}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        >
          {data?.data.map((data, index) => {
            let blacklist_active = true;
            const start = new Date(data.blacklist_start_date);

            if (start >= today) {
              blacklist_active = true;
            }

            if (data.blacklist_end_date) {
              const end = new Date(data.blacklist_end_date);
              if (end < today) {
                blacklist_active = false;
              }
            }

            return (
              <TableRow key={index}>
                <TableCell align="center">{data.person.personnal_id}</TableCell>
                <TableCell align="center">{data.person.full_name}</TableCell>
                <TableCell align="center">{data.person.person_type.type_name}</TableCell>
                <TableCell align="center">{data.person.company.company_name}</TableCell>
                <TableCell align="center">{data.blacklist_start_date}</TableCell>
                <TableCell align="center">
                  {data.blacklist_end_date ? data.blacklist_end_date : '-'}
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" color={blacklist_active ? 'success.main' : 'error'}>
                    {blacklist_active ? 'Active' : 'Inactive'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  {/* <Button variant="contained" color="success" sx={{ marginX: 0.5 }}>
                    ข้อมูล
                  </Button> */}
                  <Button
                    component={Link}
                    to={`/blacklist/${data.person_id}/edit`}
                    variant="contained"
                    color="warning"
                    sx={{ marginX: 0.5 }}
                  >
                    แก้ไข
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </Table>
      </Box>
    </>
  );
};

export default TablePersonBlacklist;
